import { useState, useEffect, useCallback } from 'react';
import type { MutableRefObject } from 'react';
import { toPng } from 'html-to-image';
import type { SnippetState, SupportedLanguage, SnippetTheme, Snippet } from './snippet.types';
import {
  generateSnippetId,
  saveSnippet,
  loadSnippet,
  buildShareUrl,
  parseSnippetIdFromHash,
} from './snippet.utils';

const DEFAULT_CODE = `// Welcome to Famini Snippet Share!
// Paste your code here and share it beautifully.

function greet(name: string): string {
  return \`Hello, \${name}!\`;
}

console.log(greet('World'));`;

export function useSnippet() {
  const [state, setState] = useState<SnippetState>({
    code: DEFAULT_CODE,
    language: 'typescript',
    theme: 'tomorrow',
    title: '',
    savedId: null,
    copyCodeStatus: 'idle',
    copyLinkStatus: 'idle',
    downloadStatus: 'idle',
  });

  // Load snippet from URL hash on mount
  useEffect(() => {
    const id = parseSnippetIdFromHash(window.location.hash);
    if (!id) return;
    const snippet = loadSnippet(id);
    if (!snippet) return;
    setState(prev => ({
      ...prev,
      code: snippet.code,
      language: snippet.language,
      theme: snippet.theme,
      title: snippet.title,
      savedId: id,
    }));
  }, []);

  const setCode = useCallback((code: string) => {
    setState(prev => ({ ...prev, code, savedId: null }));
  }, []);

  const setLanguage = useCallback((language: SupportedLanguage) => {
    setState(prev => ({ ...prev, language }));
  }, []);

  const setTheme = useCallback((theme: SnippetTheme) => {
    setState(prev => ({ ...prev, theme }));
  }, []);

  const setTitle = useCallback((title: string) => {
    setState(prev => ({ ...prev, title }));
  }, []);

  const handleCopyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(state.code);
      setState(prev => ({ ...prev, copyCodeStatus: 'copied' }));
      setTimeout(() => setState(prev => ({ ...prev, copyCodeStatus: 'idle' })), 2000);
    } catch {
      setState(prev => ({ ...prev, copyCodeStatus: 'error' }));
      setTimeout(() => setState(prev => ({ ...prev, copyCodeStatus: 'idle' })), 2000);
    }
  }, [state.code]);

  const handleSaveAndCopyLink = useCallback(async () => {
    const id = generateSnippetId();
    const snippet: Snippet = {
      id,
      code: state.code,
      language: state.language,
      theme: state.theme,
      title: state.title,
      createdAt: Date.now(),
    };
    saveSnippet(snippet);
    const url = buildShareUrl(id);
    window.location.hash = `snippet-${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setState(prev => ({ ...prev, savedId: id, copyLinkStatus: 'copied' }));
      setTimeout(() => setState(prev => ({ ...prev, copyLinkStatus: 'idle' })), 2000);
    } catch {
      setState(prev => ({ ...prev, savedId: id, copyLinkStatus: 'error' }));
      setTimeout(() => setState(prev => ({ ...prev, copyLinkStatus: 'idle' })), 2000);
    }
  }, [state.code, state.language, state.theme, state.title]);

  const handleDownload = useCallback(
    async (previewRef: MutableRefObject<HTMLDivElement | null>) => {
      if (!previewRef.current) return;
      setState(prev => ({ ...prev, downloadStatus: 'loading' }));
      try {
        const dataUrl = await toPng(previewRef.current, {
          cacheBust: true,
          pixelRatio: 2,
          backgroundColor: '#1e293b',
        });
        const link = document.createElement('a');
        link.download = `${state.title || 'snippet'}-${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
        setState(prev => ({ ...prev, downloadStatus: 'done' }));
        setTimeout(() => setState(prev => ({ ...prev, downloadStatus: 'idle' })), 2000);
      } catch {
        setState(prev => ({ ...prev, downloadStatus: 'error' }));
        setTimeout(() => setState(prev => ({ ...prev, downloadStatus: 'idle' })), 2000);
      }
    },
    [state.title],
  );

  return {
    ...state,
    setCode,
    setLanguage,
    setTheme,
    setTitle,
    handleCopyCode,
    handleSaveAndCopyLink,
    handleDownload,
  };
}
